import React, { createContext } from "react";
import { noop } from "./util";

/**
 * 页面接口定义
 */

interface RESCUE_MESSAGES {
  name: string;
  value: string;
}

/**
 * 页面常量定义
 */

// 高德地图 Key
export const AMAP_KEY: string = "e9c4e1537725c67ae982d59e02c6eca4";
// 高德地图版本号
export const AMAP_VERSION: string = "2.0"; // 1.4.15
export const AMAP_UI_VERSION: string = "1.1";

// 高德地图插件
export const AMAP_PLUGINS: string[] = [
  "AMap.ToolBar",
  "AMap.ControlBar",
  "AMap.Geolocation",
  "AMap.Driving",
];

export const AMAP_SCRIPT_CALLBACK_NAME = "__amap_init_callback";

export const AMAP_MAIN_SCRIPT_URL = `//webapi.amap.com/maps?v=${AMAP_VERSION}&key=${AMAP_KEY}&callback=${AMAP_SCRIPT_CALLBACK_NAME}&plugin=${AMAP_PLUGINS.join(",")}`;

export const AMAP_UI_SCRIPT_URL = `//webapi.amap.com/ui/${AMAP_UI_VERSION}/main-async.js`;

interface IContext {
  state: any;
  dispatch: any;
}
// 创建一个上下文
export const Context = createContext<IContext>({
  state: {},
  dispatch: noop,
});

export const INIT_STATE = {
  uploadedImageList: {},
  createOrderState: 0,
};

// 默认客服号码（配置接口不可用时兜底用）
export const DEFAULT_SERVICE_CALL_NUMBER = "4007836688";

// 文件上传格式
export const ALLOW_IMG_FILE_TYPE = "jpg, jpeg, png";
export const ALLOW_IMG_FILE_MAX_SIZE = 1024 * 1024 * 10;

export const CATEGORY_LIST = ["FRONT", "BACK", "WHEEL"];
// 上传图片分类类型
export const UPLOAD_CATEGORY_TYPE = {
  FRONT: {
    title: "前方45度",
    value: "01",
  },
  BACK: {
    title: "后方",
    value: "02",
  },
  WHEEL: {
    title: "车轮",
    value: "03",
  },
};

// 每个分类最大上传图片数量
export const MAX_UPLOAD_COUNT_PER_CATEGORY = 3;

export enum RESCUE_REASON {
  TYRE_DEFECT = "003",
  LOW_BATTERY = "002",
  VEH_BREAKDOWN = "005",
  TRAFFIC_ACCIDENT = "004",
}

export const RESCUE_REASONS: RESCUE_MESSAGES[] = [
  { name: "轮胎原因", value: RESCUE_REASON.TYRE_DEFECT },
  { name: "车辆电量不足", value: RESCUE_REASON.LOW_BATTERY },
  { name: "车辆故障", value: RESCUE_REASON.VEH_BREAKDOWN },
  { name: "交通事故", value: RESCUE_REASON.TRAFFIC_ACCIDENT },
];

// 救援原因子选项
export const RESCUE_REASON_SUB_OPTIONS: Record<string, RESCUE_MESSAGES[]> = {
  [RESCUE_REASON.TRAFFIC_ACCIDENT]: [
    {
      name: "碰撞/追尾",
      value: "001",
    },
    {
      name: "底盘剐蹭",
      value: "002",
    },
    {
      name: "车辆涉水",
      value: "003",
    },
    {
      name: "车辆陷入困境",
      value: "004",
    },
  ],
  [RESCUE_REASON.TYRE_DEFECT]: [
    {
      name: "爆胎/破损",
      value: "001",
    },
    {
      name: "扎钉",
      value: "002",
    },
    {
      name: "漏气",
      value: "003",
    },
  ],
};

// 救援原因名称映射
export const RESCUE_REASON_NAMES: Record<string, string> = {
  [RESCUE_REASON.TYRE_DEFECT]: "轮胎问题",
  [RESCUE_REASON.LOW_BATTERY]: "车辆电量不足",
  [RESCUE_REASON.VEH_BREAKDOWN]: "车辆故障",
  [RESCUE_REASON.TRAFFIC_ACCIDENT]: "交通事故",
};

// 车辆状况描述占位符映射
export const VEHICLE_DESCRIPTION_PLACEHOLDERS: Record<string, string> = {
  [RESCUE_REASON.TYRE_DEFECT]: "请描述车辆压胎情况", // 轮胎问题
  [RESCUE_REASON.LOW_BATTERY]: "请描述剩余续航里程", // 车辆电量不足
  [RESCUE_REASON.VEH_BREAKDOWN]: "请描述车辆故障具体情况", // 车辆故障
  [RESCUE_REASON.TRAFFIC_ACCIDENT]: "请描述车辆受损情况", // 交通事故
};
export const IF_SHIFT_TON: RESCUE_MESSAGES[] = [
  { name: "可以挂N档/解除手刹", value: "1" },
  { name: "无法挂N档/解除手刹", value: "0" },
];
export const IF_TOWBAR: RESCUE_MESSAGES[] = [
  { name: "有拖车钩", value: "1" },
  { name: "无拖车钩", value: "0" },
];
export const CAR_POSITIONS: RESCUE_MESSAGES[] = [
  { name: "开阔地面", value: "01" },
  { name: "地下车库", value: "02" },
  //   { name: "周围有阻碍的其他地方", value: "03" },
];

export enum RESCUE_TYPE {
  IMMEDIATE = "timelyRescue", // 立即救援
  LATER = "reservationRescue", // 稍后救援
}

export const RESCUE_TYPE_OPTIONS = [
  { name: "立即救援", value: RESCUE_TYPE.IMMEDIATE },
  { name: "稍后救援", value: RESCUE_TYPE.LATER },
];

export const LOAD_STATUS = {
  NULL: -1, // 未开始
  PENDING: 0, // 请求中。。。
  SUCCESS: 200, // 加载成功
  FAIL: 400, // 加载失败
  ERROR: 404, // 加载异常
  TIMEOUT: 504, // 超时
  BAD_REQUEST: 500, // 服务端报错
};

// actions set
export const ACTION_NAME = {
  CREATE_RESCUE_ORDER: "ACTION_NAME-CREATE_RESCUE_ORDER",
  SET_USER_INFO_DATA: "ACTION_NAME-SET_USER_INFO_DATA",
  SET_PAGE_CONFIG_DATA: "ACTION_NAME-SET_PAGE_CONFIG_DATA",
  SET_UPLOAD_IMGS_DATA: "ACTION_NAME-SET_UPLOAD_IMGS_DATA",
  RESET_FORM_DATA: "ACTION_NAME-RESET_FORM_DATA",
};

// 工单信息轮询时间间隔
export const ORDER_STATE_INTERVAL_TIME = 60;
// 地图车辆信息轮询时间间隔
export const DRAW_MAP_INTERVAL_TIME = 5;

// 小屏兼容阈值
export const MIN_SCREEN_HEIGHT = 480;

export const RESCUE_STATE_SET = {
  RECEIVED: {
    value: "01",
    title: "救援确认中",
    desc: "",
  },
  CONFIRMED: {
    value: "02",
    title: "救援已确认",
    desc: "",
  },
  COMING: {
    value: "03",
    title: "救援车在途",
    desc: "",
  },
  ARRIVED: {
    value: "04",
    title: "救援到达",
    desc: "",
  },
};

export enum RESCUE_STATE {
  dispatching = "dispatching", // 救援商分派中
  rescuing = "rescuing", // 救援中
  waitingSubmitRescuer = "waitingSubmitRescuer", // 待提交救援商
  cancelled = "cancelled", // 已取消
  completed = "completed", // 已完成
}

export const defaultRescueTextData = {
  title: "救援已提交",
  tip: "已安排专人跟进救援请求，请耐心等待",
};

export const cancelTip = "正在核实取消救援详情，请耐心等待";

export const RESCUE_STATE_TEXT_MAP: Record<
  string,
  { title: string; tip: string }
> = {
  [RESCUE_STATE.waitingSubmitRescuer]: defaultRescueTextData,
  [RESCUE_STATE.dispatching]: defaultRescueTextData,
  [RESCUE_STATE.rescuing]: {
    title: "救援中",
    tip: "救援团队正全力赶往您的位置，请耐心等待",
  },
};

// 取消救援原因列表
export const CANCEL_REASONS = [
  { label: "故障恢复", value: "故障恢复" },
  { label: "已行驶到充电站", value: "已行驶到充电站" },
  { label: "已行驶到维修点", value: "已行驶到维修点" },
  { label: "保险安排救援", value: "保险安排救援" },
  { label: "事故未处理好", value: "事故未处理好" },
  { label: "自行解决", value: "自行解决" },
  { label: "当前所在路段无法进入", value: "当前所在路段无法进入" },
  { label: "等待时效太长", value: "等待时效太长" },
  { label: "其他原因", value: "其他原因" },
];

// 异常码枚举集合
export const ERROR_CODE_MAP = {
  NEED_LOGIN: 12150008, // 登录失效异常码，需要提示登录 12150008
  ORDER_REPEAT: 48800024, // 订单重复创建
};

export const COMMON_ERROR_MSG = (
  <div className="common-error-msg">
    网络问题，无法进入线上救援流程，请拨打救援热线：
    <p>
      <a className="phone-call-num" href={`tel:${4007836688}`}>
        4007836688
      </a>
    </p>
  </div>
);

export const NEED_LOGIN_ERROR_MSG = (
  <div className="common-error-msg">
    请登录后再使用，救援电话：
    <p>
      <a className="phone-call-num" href={`tel:${4007836688}`}>
        4007836688
      </a>
    </p>
  </div>
);
